import { isEmpty, throttle } from 'lodash';
import { armsErrorSend } from './armsErrorSend';
import { GlobalTracker } from '../Tracker';

interface CustomElement extends HTMLElement {
  __observerEnter?: boolean; // 使用可选属性
  __startTime?: number;
}

/**
 * 【方法请放在useEffcet或者其他只执行一次的方法体中。】
 * 监听组件出现在页面上进行埋点方法上报 使用方法参考下方useExposureOverviewModule
 * @return Observer 推荐在return 时 disconnect
 * @param {NodeList | Node} doms 需要被监听的dom节点 或者dom节点数组
 * @param onEnterViewport (dom,observeObject) 被监听的dom节点、监听对象(常用属性isIntersecting、intersectionRatio可以用于判断出现在屏幕上的百分比)
 * @param onExitViewPort 注意只有enter过才会调用exit。 原本初次渲染时判断逻辑被中断：原本在首次监听时会判断一次dom节点距离，调用onExitViewPort。
 * @param onError 兼容性不允许的情况，回调方法，可以自行全部上报，或者不做任何处理
 * @param once 是否只触发一次(默认false)
 * @param observerOptions root?,rootMargin?,threshold? 默认Y=0, X=0, threshold默认
 */
export function exposureTracker({
  doms: _doms,
  onEnterViewport,
  onExitViewPort,
  onError,
  once = false,
  observerOptions = {},
}: {
  doms: NodeList | Node;
  onEnterViewport?: (target: CustomElement, observerEntry: IntersectionObserverEntry) => void;
  onExitViewPort?: (target: CustomElement, observerEntry: IntersectionObserverEntry) => void;
  onError?: () => void;
  once?: boolean;
  observerOptions?: IntersectionObserverInit;
}) {
  if (isEmpty(_doms)) return armsErrorSend('warning: no useExposureTracker doms', { filename: 'useExposureTracker' });

  try {
    const doms = Array.isArray(_doms) || _doms instanceof NodeList ? _doms : [_doms];
    const { root, rootMargin, threshold } = observerOptions;
    if ('IntersectionObserver' in window) {
      const Observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((element) => {
            const dom = element.target;

            try {
              if (element.isIntersecting) {
                (element.target as CustomElement).__observerEnter = true;
                onEnterViewport?.(element.target as CustomElement, element);
              } else if ((element.target as CustomElement).__observerEnter) {
                onExitViewPort?.(element.target as CustomElement, element);
                if (once) {
                  Observer.unobserve(dom);
                }
              }
            } catch (e) {
              Observer.unobserve(dom);
              armsErrorSend(e as string, { filename: 'useExposureTracker observe' });
            }
          });
        },
        {
          root: root || document.getElementById('_next'),
          rootMargin: rootMargin || '0px',
          threshold: threshold || 0,
        }
      );

      doms.forEach((dom) => {
        Observer.observe(dom);
      });

      return Observer;
    } else {
      onError?.();
    }
  } catch (e) {
    armsErrorSend(e as Error | string, { filename: 'useExposureTracker' });
  }
}

/**
 * 【方法请放在useEffcet或者其他只执行一次的方法体中。】
 * 产品页面Overview模块使用的曝光监听方法，查询带有data-overview-observer=true的dom节点并监听
 * 默认页面上可视度在0.2以下的组件为“看不到的”
 * @param once 是否是一次性的,默认为true
 * @param doms 默认取值 data-overview-observer=true 优先取值特别传入的dom节点
 * @param value [建议传入url_key]
 * @param observerOptions root?,rootMargin?,threshold? 默认Y=0, X=0, threshold[0.2]
 *
 * @param type 都是上报到同一个 data-category下，上报action拼接 data-action + type。 目前可以取值：【expose_time】同时记录曝光时长
 * @param category 取dom节点上的 data-category 默认为 product_page_overview
 * @param action 取dom节点上的 data-action 默认在后面加上_expose
 * @param label 取dom节点上的 data-label
 * @param value 默认取dom节点上的data-value,没有的话取传入的propsValue
 */
export function exposureOverviewModule({
  doms,
  once = true,
  value: propsValue,
  observerOptions: propsOptions,
}: {
  doms: NodeList;
  once?: boolean;
  value?: number;
  observerOptions: IntersectionObserverInit;
}) {
  const observerList = doms || document.querySelectorAll('[data-overview-observer=true]:not([data-tracked="1"])');
  const onEnterViewport = (dom: CustomElement) => {
    const {
      type = '',
      category = 'product_page_overview',
      action = '',
      label = '',
      value = propsValue,
    } = dom.dataset || {};
    if (!action)
      return console.error('有一个DOM节点你忘记加action了虽然不知道是哪一个但是请排查一下 className: ' + dom.className);
    if (once) {
      // useExposureTracker已经限制了once只监听一次，但是避免多个组件中反复调用useExposureTracker 方法重新加上监听的情况。
      dom.dataset.tracked = '1';
    }

    if (type === 'expose_time') {
      dom.__startTime = new Date().valueOf();
    }
    // @ts-expect-error GlobalTracker方法还未TS化, 不影响使用
    GlobalTracker.Common({ category, action: `${action}_expose`, label, value });
    //
    // if (['overview_sellingpoint', 'description_bulletpoint'].includes(action)) {
    //   ThirdPartyTracker.ABTastyTracker.sendEvent({ KPI: `${propsValue}_expose_overview_sellingpoint` });
    // }
  };

  const onExitViewPort = (dom: CustomElement) => {
    const { type = '', category = 'product_page_overview', action = '', label = '' } = dom.dataset || {};

    if (type === 'expose_time' && dom.__startTime) {
      const stay_time = ((new Date().valueOf() - dom.__startTime) / 1000).toFixed(2);
      dom.__startTime = 0;
      // @ts-expect-error value传string也是可以的
      GlobalTracker.Common({ category, action: `${action}_${type}`, label, value: stay_time });
    }
  };

  const observerOptions = propsOptions || { threshold: [0.1] };

  exposureTracker({ doms: observerList, onEnterViewport, onExitViewPort, once, observerOptions });
}

/**
 * 【方法请放在useEffcet或者其他只执行一次的方法体中。】
 * 记录上报页面滚动时当前监听组件在页面上的浏览深度，浏览完成后将移除监听。
 * @param dom 上报节点
 * @param once 默认只上报一次。
 * @param category
 * @param action
 * @param label
 * @param value
 */
export function trackElementScrollDepth({
  once = true,
  dom,
  category = '',
  action = '',
  label = null,
  value,
}: {
  once?: boolean;
  dom: HTMLElement | null;
  category?: string;
  action?: string;
  label?: string | null;
  value?: number;
}) {
  if (!dom || !category || !action)
    return console.error('trackelementscrolldepth方法dom节点为空，或者忘记加category、action了', label);
  try {
    const domHeight = dom.clientHeight;
    const windowHeight = window.innerHeight || document.body.clientHeight;
    const recordPercent: number[] = [];
    const onScroll = throttle(() => {
      const elemTop = dom.getBoundingClientRect().top;
      const viewHeight = elemTop > 0 ? windowHeight - elemTop : windowHeight + Math.abs(elemTop);
      const viewPercent = Number(((viewHeight / domHeight) * 10).toFixed(0)) * 10;
      if (viewPercent > 0 && viewPercent <= 100) {
        if (!recordPercent.includes(viewPercent)) {
          recordPercent.push(viewPercent);
          // @ts-expect-error GlobalTracker方法还未TS化, 不影响使用
          GlobalTracker.Common({ category, action, label: `${viewPercent}%`, value });
        }
      }

      if (once && viewPercent === 100) {
        window.removeEventListener('scroll', onScroll);
      }
    }, 200);

    window.addEventListener('scroll', onScroll);
  } catch (e) {
    armsErrorSend(e as Error | string, { filename: 'trackElementScrollDepth' });
  }
}

export default exposureTracker;
